import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CATEGORIES, PLATFORMS, CAMPAIGN_TYPES, LANGUAGES, COUNTRIES, BUDGET_RANGES, formatMoney } from "@/lib/constants";
import { Compass, Search, Gavel, MapPin, ArrowRight, X } from "lucide-react";

const ALL = "__all__";

export default function Discover() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [campaignType, setCampaignType] = useState(ALL);
  const [language, setLanguage] = useState(ALL);
  const [country, setCountry] = useState(ALL);
  const [city, setCity] = useState("");
  const [budget, setBudget] = useState("0");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category !== ALL) params.category = category;
    if (platform !== ALL) params.platform = platform;
    if (campaignType !== ALL) params.campaign_type = campaignType;
    if (language !== ALL) params.language = language;
    if (country !== ALL) params.country = country;
    if (city) params.city = city;
    const br = BUDGET_RANGES[Number(budget)];
    if (br && br.min != null) params.min_budget = br.min;
    if (br && br.max != null) params.max_budget = br.max;
    const res = await api.get("/campaigns", { params });
    setCampaigns(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, platform, campaignType, language, country, budget]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, city]);

  const clear = () => { setSearch(""); setCategory(ALL); setPlatform(ALL); setCampaignType(ALL); setLanguage(ALL); setCountry(ALL); setCity(""); setBudget("0"); };
  const hasFilters = search || category !== ALL || platform !== ALL || campaignType !== ALL || language !== ALL || country !== ALL || city || budget !== "0";

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">Marketplace</p>
          <h1 className="font-heading font-black text-4xl tracking-tight flex items-center gap-3"><Compass className="h-8 w-8 text-neon-lime" /> Discover campaigns</h1>
          <p className="text-muted-foreground mt-2">Browse open campaigns and submit your bid.</p>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-4 mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="discover-search-input" placeholder="Search campaigns…" className="bg-background border-border h-11 pl-9" />
            </div>
            {hasFilters && <button onClick={clear} data-testid="clear-filters-btn" className="flex items-center gap-1 text-sm text-brand-pink hover:opacity-70 px-3 shrink-0"><X className="h-4 w-4" /> Clear</button>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="discover-category-filter" className="bg-background border-border h-11"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="discover-platform-filter" className="bg-background border-border h-11"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All platforms</SelectItem>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={campaignType} onValueChange={setCampaignType}>
              <SelectTrigger data-testid="discover-type-filter" className="bg-background border-border h-11"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All types</SelectItem>{CAMPAIGN_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="discover-language-filter" className="bg-background border-border h-11"><SelectValue placeholder="Language" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All languages</SelectItem>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="discover-country-filter" className="bg-background border-border h-11"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All countries</SelectItem>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="discover-city-filter" placeholder="City" className="bg-background border-border h-11" />
            <Select value={budget} onValueChange={setBudget}>
              <SelectTrigger data-testid="discover-budget-filter" className="bg-background border-border h-11"><SelectValue placeholder="Budget" /></SelectTrigger>
              <SelectContent>{BUDGET_RANGES.map((b, i) => <SelectItem key={i} value={String(i)}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading campaigns…</p>
        ) : campaigns.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-surface p-12 text-center">
            <Compass className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="font-heading font-bold text-xl mb-2">No open campaigns found</p>
            <p className="text-muted-foreground">Try adjusting your filters or check back soon.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {campaigns.map((c, i) => (
              <motion.div key={c.campaign_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/campaigns/${c.campaign_id}`} data-testid={`discover-campaign-${c.campaign_id}`}
                  className="group block rounded-2xl border border-border bg-surface p-6 hover:bg-surfaceHover transition-colors h-full">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {c.category && <Badge variant="outline" className="border-neon-lime/30 text-neon-lime bg-neon-lime/10">{c.category}</Badge>}
                    {c.campaign_type && <Badge variant="outline" className="border-border text-muted-foreground">{c.campaign_type}</Badge>}
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-2 group-hover:text-neon-lime transition-colors">{c.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{c.objective}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(c.platforms || []).map((p) => <span key={p} className="text-xs px-2.5 py-1 rounded-full bg-background border border-border text-muted-foreground">{p}</span>)}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <p className="font-heading font-black text-xl">{c.compensation_type === "gift_hamper" ? `🎁 ${formatMoney(c.hamper_value, c.currency)}` : formatMoney(c.budget, c.currency)}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                        <span className="flex items-center gap-1"><Gavel className="h-3 w-3" /> {c.bids_count} bids</span>
                        {c.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.location}</span>}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-neon-lime group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
